// Sincronização do progresso com o academy-service (F2).
//
// Modelo: o SERVIDOR é a fonte da verdade; o localStorage vira cache local —
// garante resposta instantânea na UI e tolera ficar offline. Fluxo:
//   1. Ao abrir a trilha: baixa o progresso do servidor e sobrescreve o cache
//      (se o servidor está zerado e o local tem progresso do F1, migra uma
//      única vez via PUT /progresso/importar).
//   2. Ao concluir lição OU revisão: grava local (UX imediata) e envia ao
//      servidor com request_id idempotente; depois adota o estado do servidor.
//   3. Sem VITE_ACADEMY_URL (ou servidor fora), tudo segue 100% local.
import {
    AcademyApiService,
    ConcluirLicaoPayload,
    ConcluirRevisaoPayload,
    ProgressoServidor,
} from '../../services/AcademyApi.service'
import {
    Progresso,
    ResultadoPilula,
    lerProgresso,
    sobrescreverProgresso,
} from './dominio'

export function academyDisponivel(): boolean {
    return Boolean(import.meta.env.VITE_ACADEMY_URL)
}

const api = () => new AcademyApiService()

/* ------------------------------------------------------------------ outbox
   Conclusões que ainda não chegaram ao servidor (offline, deploy, erro).
   Cada item guarda o request_id fixado no momento da conclusão — reenviar é
   seguro porque o servidor é idempotente por request_id. O cache local NUNCA
   é sobrescrito pelo servidor enquanto houver pendência na fila (senão
   progresso feito offline sumiria). */

const OUTBOX_KEY = 'licito.trilhaGuru.outbox.v1'

// Item da fila = o payload do contrato mais o discriminador `tipo`, que diz a
// qual endpoint ele pertence. O `tipo` é do envelope, não do contrato: é
// retirado antes do POST.
type ItemOutbox =
    | ({tipo: 'licao'} & ConcluirLicaoPayload)
    | ({tipo: 'revisao'} & ConcluirRevisaoPayload)

// Filas gravadas antes da revisão ter endpoint guardam o payload de lição
// cru, sem `tipo`. Elas continuam na mesma chave (descartá-las perderia
// conclusões feitas offline por quem só agora atualizou a aba), então a
// ausência do campo é lida como o único tipo que existia: lição.
function comTipo(bruto: unknown): ItemOutbox | null {
    if (!bruto || typeof bruto !== 'object') return null
    const item = bruto as Record<string, unknown>
    if (typeof item.request_id !== 'string') return null
    if (item.tipo === 'revisao') return item as unknown as ItemOutbox
    return {...item, tipo: 'licao'} as unknown as ItemOutbox
}

function lerOutbox(): ItemOutbox[] {
    try {
        const fila = JSON.parse(localStorage.getItem(OUTBOX_KEY) ?? '[]')
        if (!Array.isArray(fila)) return []
        return fila
            .map(comTipo)
            .filter((item): item is ItemOutbox => item !== null)
    } catch {
        return []
    }
}

function salvarOutbox(fila: ItemOutbox[]) {
    try {
        if (fila.length === 0) localStorage.removeItem(OUTBOX_KEY)
        else localStorage.setItem(OUTBOX_KEY, JSON.stringify(fila))
    } catch {
        /* noop */
    }
}

function enfileirar(item: ItemOutbox) {
    salvarOutbox([...lerOutbox(), item])
}

// Manda o item ao endpoint do seu tipo, sem o `tipo` no corpo — o servidor
// recebe exatamente o payload do contrato.
function enviar(item: ItemOutbox): Promise<unknown> {
    if (item.tipo === 'revisao') {
        const {tipo: _, ...payload} = item
        return api().concluirRevisao(payload)
    }
    const {tipo: _, ...payload} = item
    return api().concluirLicao(payload)
}

// Reenvia a fila em ordem; para na primeira falha (mantém o restante).
// Relê a fila a cada passo para incorporar itens enfileirados durante o
// dreno. Lock de módulo evita drenos concorrentes duplicando envios.
let drenoEmAndamento: Promise<boolean> | null = null

function drenarOutbox(): Promise<boolean> {
    if (!drenoEmAndamento) {
        drenoEmAndamento = (async () => {
            for (;;) {
                const fila = lerOutbox()
                if (fila.length === 0) return true
                try {
                    await enviar(fila[0])
                } catch {
                    return false
                }
                salvarOutbox(
                    lerOutbox().filter(
                        (i) => i.request_id !== fila[0].request_id
                    )
                )
            }
        })().finally(() => {
            drenoEmAndamento = null
        })
    }
    return drenoEmAndamento
}

function servidorParaLocal(doc: ProgressoServidor): Progresso {
    const local = lerProgresso()
    return {
        // O XP do servidor é o XP, ponto: com /revisoes/concluir ele passou a
        // contabilizar também a revisão dos erros. Nada de parcela local
        // reaplicada por cima (ver a nota de migração em `lerProgresso`).
        xp: doc.xp,
        concluidas: doc.concluidas ?? {},
        dominadas: Object.fromEntries(
            (doc.dominadas ?? []).map((id) => [id, true as const])
        ),
        streak: {
            atual: doc.streak?.atual ?? 0,
            recorde: doc.streak?.recorde ?? 0,
            ultimoDia: doc.streak?.ultimo_dia ?? null,
        },
        tamanhos: doc.tamanhos ?? {},
        // Stats vêm do servidor desde a migração 0004 — é o que faz a
        // redenção (acertar a revisão) valer por CONTA e não por navegador.
        // Doc sem o campo = servidor anterior à 0004: o local segue dono,
        // para um deploy fora de ordem não apagar o card de revisão.
        stats: doc.stats ?? local.stats,
    }
}

function localParaServidor(p: Progresso) {
    return {
        // O total local INTEIRO, incluindo o que a revisão rendeu enquanto ela
        // era local-only: é esta importação que leva esse XP para o servidor,
        // e a partir dela a revisão é contabilizada lá como qualquer lição.
        xp: p.xp,
        concluidas: p.concluidas,
        dominadas: Object.keys(p.dominadas),
        streak: {
            atual: p.streak.atual,
            recorde: p.streak.recorde,
            ultimo_dia: p.streak.ultimoDia,
        },
        tamanhos: p.tamanhos ?? {},
        // Os stats locais são a única fonte do que foi errado na era
        // local-only — a importação é a chance de levá-los para a conta.
        stats: p.stats ?? {},
    }
}

// Sobrescreve o cache local com o doc do servidor — mas SÓ se a fila estiver
// vazia NESTE momento. A checagem no início do fluxo não basta: uma conclusão
// pode entrar durante o GET (outra aba, ou a própria), e sobrescrever agora
// jogaria fora progresso que ainda nem foi entregue. Quem ficou de fora será
// adotado na próxima sincronização, depois que o dreno entregar a fila.
function adotarSeSemPendencias(doc: ProgressoServidor): boolean {
    if (lerOutbox().length > 0) return false
    sobrescreverProgresso(servidorParaLocal(doc))
    return true
}

// 409 na importação é esperado (progresso já existe no servidor — corrida de
// duas abas ou retry); qualquer outra falha é real e NÃO pode deixar o fluxo
// seguir para o GET: o servidor devolveria zeros e o sobrescrever apagaria o
// progresso local exatamente na janela de migração.
function eConflito(erro: unknown): boolean {
    return (
        typeof erro === 'object' &&
        erro !== null &&
        (erro as {response?: {status?: number}}).response?.status === 409
    )
}

// Sincroniza com o servidor: primeiro DRENA a outbox (reenvio idempotente
// das conclusões pendentes); só com a fila vazia o estado do servidor
// sobrescreve o cache local. Migra o progresso F1 (local-only) na primeira
// sincronização. Retorna true se sincronizou; false se segue no local
// (offline ou pendências ainda não entregues).
export async function sincronizarProgresso(): Promise<boolean> {
    if (!academyDisponivel()) return false
    try {
        const entregue = await drenarOutbox()
        if (!entregue) return false
        let doc = await api().getProgresso()
        const local = lerProgresso()
        if (doc.xp === 0 && local.xp > 0) {
            try {
                await api().importarProgresso(localParaServidor(local))
            } catch (erro) {
                // 409: já existia progresso no servidor — usa o dele. Outra
                // falha (422, 500, timeout) aborta ANTES do GET: seguir
                // adiante sobrescreveria meses de progresso local com zeros.
                if (!eConflito(erro)) return false
            }
            doc = await api().getProgresso()
        }
        return adotarSeSemPendencias(doc)
    } catch {
        return false
    }
}

function paraServidor(resultados: ResultadoPilula[]) {
    return resultados.map((x) => ({pilula_id: x.id, primeira: x.primeira}))
}

// Enfileira e tenta entregar em background (fire-and-forget com request_id
// idempotente): a UI mostra o resultado local na hora; o estado autoritativo
// do servidor sobrescreve o cache logo em seguida, sem bloquear. Offline/erro:
// o cache local segue valendo e a próxima sincronização reconcilia.
function propagar(item: ItemOutbox): void {
    // entra na fila ANTES de tentar enviar: falha de rede nunca perde nada
    enfileirar(item)
    drenarOutbox()
        .then((entregue) => (entregue ? api().getProgresso() : null))
        .then((doc) => {
            if (doc) adotarSeSemPendencias(doc)
        })
        .catch(() => {
            /* offline: cache local segue valendo; outbox reenvia depois */
        })
}

// Conclusão de lição do MAPA: leva slug e tamanho da seção, que é o par pelo
// qual o servidor reconcilia `concluidas`.
export function propagarConclusao(
    slug: string,
    licao: number,
    tamanhoSecao: number,
    resultados: ResultadoPilula[]
): void {
    if (!academyDisponivel()) return
    propagar({
        tipo: 'licao',
        request_id: crypto.randomUUID(),
        secao_slug: slug,
        licao,
        tamanho_secao: tamanhoSecao,
        resultados: paraServidor(resultados),
    })
}

// Conclusão de sessão de REVISÃO: só os resultados. Vai para
// /revisoes/concluir — nunca para /licoes/concluir, que marcaria uma lição do
// mapa e reconciliaria uma seção que a revisão sequer tem.
export function propagarRevisao(resultados: ResultadoPilula[]): void {
    if (!academyDisponivel()) return
    propagar({
        tipo: 'revisao',
        request_id: crypto.randomUUID(),
        resultados: paraServidor(resultados),
    })
}
